<template>
  <div class="log-in">
    <v-dialog
      v-model="showLogInDialog"
      max-width="400px"
      persistent
      :fullscreen="breakpoint.xsOnly"
      :hide-overlay="breakpoint.xsOnly"
    >
      <v-card rounded center>
        <v-toolbar dense flat color="grey darken-3" dark>
          <v-toolbar-title>Log in</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showLogInDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text v-show="showLogInForm">
          <v-form ref="form" teal>
            <v-text-field
              v-model="logInEmail"
              label="E-mail"
              color="teal"
              class="mt-2"
              autofocus
              :rules="logInEmailRules"
              @keyup.enter="logInWithMail"
            ></v-text-field>

            <v-text-field
              v-model="logInPassword"
              label="Password"
              type="password"
              color="teal"
              class="mt-4"
              @keyup.enter="logInWithMail"
            ></v-text-field>
            <div
              class="red--text text-xs-center mt-2 mb-4"
              v-show="feedback"
            >
              {{ feedback }}
            </div>
            <v-btn
              class="mt-2 mb-4"
              block
              color="teal"
              dark
              large
              @click="logInWithMail"
              >
              <span v-show="!showEmailLoginSpinner">Log in</span>
              
              <v-progress-circular
                v-show="showEmailLoginSpinner"
                size="28"
                width="3"
                indeterminate
                color="teal lighten-3"> 
              </v-progress-circular>
              
              </v-btn
            >

            <p class="continue-with mt-4">
              <span>Or continue with</span>
            </p>
            <v-btn
              class="mt-8"
              color="blue darken-2"
              dark
              block
              large
              @click="logInWithProvider('facebook', 'login')"
            >
              <span v-show="!showFacebookLoginSpinner">Facebook</span>
              <v-progress-circular
                v-show="showFacebookLoginSpinner"
                size="28"
                width="3"
                indeterminate
                color="blue lighten-3"> 
              </v-progress-circular>
            </v-btn>

            <v-btn
              class="mt-4"
              color="red darken-1"
              dark
              block
              large
              @click="logInWithProvider('google', 'login')"
            >
              <span v-show="!showGoogleLoginSpinner">Google</span>
              <v-progress-circular
                v-show="showGoogleLoginSpinner"
                size="28"
                width="3"
                indeterminate
                color="red lighten-3"> 
              </v-progress-circular>
            </v-btn>
          </v-form>
          <p class="text-xs-center caption mt-3 mb-0">
            <a @click.prevent="forgotPassword">Forgot password?</a>
          </p>
        </v-card-text>

        <!-- USer still has not verified email on login -->
        <v-card-text v-show="showEmailVerificationError" class="text-xs-center">
          <p>
            You still havent verified your email address. Check your email and
            verify by clicking the link.
          </p>
          <p class="red--text">Important! After you have verified your email click OK and sign in again!</p>
          <p>
            Didn't get the email?
            <a @click.prevent="resendVerificationEmail"
              >Resend verification email.</a
            >
          </p>
          <v-btn @click="closeDialogAndSignOut" color="teal" dark large
            >OK</v-btn
          >
        </v-card-text>

        <!-- Notification that email verification has been re sent -->
        <v-card-text v-show="emailVerificationResent" class="text-xs-center">
          <p>We sent you another email.</p>
          <p class="red--text">Important! After you have verified your email click OK and sign in again!</p>
          <p>
            Still didn't receive it?
            <a href="https://www.earbeater.com/contact/" target="_blank"
              >Contact support.</a
            >
          </p>
          <v-btn @click="closeDialogAndSignOut" color="teal" dark large
            >OK</v-btn
          >
        </v-card-text>

        <!-- Forgot Password Form -->
        <v-card-text v-show="showForgotPasswordForm" class="text-xs-center">
          <p>
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
          <v-form ref="resetPasswordForm">
            <v-text-field
              v-model="logInEmail"
              label="E-mail"
              color="teal"
              class="mt-2"
              autofocus
              :rules="logInEmailRules"
              @keyup.enter="sendPasswordResetEmail"
            ></v-text-field>
            <p class="red--text mt-2">{{ forgotPasswordFeedback }}</p>
            <v-btn
              class="mt-2 mb-2"
              block
              color="teal"
              dark
              large
              @click="sendPasswordResetEmail"
              >Reset Password</v-btn
            >
          </v-form>
        </v-card-text>

        <v-card-text v-show="showResentMailConfirmation" class="text-xs-center">
          <p>Password reset email has been sent to {{ logInEmail }}</p>
          <p>Click on the link to reset your password.</p>
          <v-btn
            class="mt-2 mb-2"
            color="teal"
            dark
            large
            @click="showLogInDialog = false"
            >Ok</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import firebase from "firebase/app";
import db from "@/firebase/init";
import setupNewUserInDatabase from "@/mixins/setupNewUserInDatabase";
import logInWithProvider from "@/mixins/logInWithProvider";
export default {
  name: "LogIn",
  mixins: [setupNewUserInDatabase, logInWithProvider],
  data() {
    return {
      logInEmail: "",
      logInPassword: "",
      feedback: null,
      showLogInForm: true,
      showEmailVerificationError: false,
      emailVerificationResent: false,
      showForgotPasswordForm: false,
      showResentMailConfirmation: false,
      forgotPasswordFeedback: null,
      showEmailLoginSpinner: false,
      showFacebookLoginSpinner: false,
      showGoogleLoginSpinner: false,
      logInEmailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
    };
  },
  methods: {
    logInWithMail() {
      // validate fields
      if (this.logInEmail.length === 0 || this.logInPassword.length === 0) {
        this.feedback = "You must enter an email address and a password";
        return;
      }
      // Set spinner
      this.showEmailLoginSpinner = true;

      return firebase
        .auth()
        .signInWithEmailAndPassword(this.logInEmail, this.logInPassword)
        .then((cred) => {
          
          this.$store.dispatch('setSnackbar', {
            timeout: 3000,
            text: 'You have logged in. Welcome!'
          });

          if (cred.user.emailVerified) {
            // Check if user already has a database record
            let userRef = db.collection("users").doc(cred.user.uid);
            userRef
              .get()
              .then((doc) => {
                if (doc.exists) {
                  console.log(
                    "User exists already.. Will not set up new user in database"
                  );

                  // Redirect here
                } else {
                  // Create user record
                  this.setupNewUserInDatabase(cred.user);
                }
                // Remove dialog and spinner
                this.showLogInDialog = false;
                this.showEmailLoginSpinner = false;
              })
              .catch((err) => {
                console.log("Error getting document", err.message);
              });
          } else {
            this.showLogInForm = false;
            this.showEmailVerificationError = true;
            // set global var that user is not verified and guard routes based on that ???
          }
        })
        .catch((err) => {          
          this.feedback = err.message;
          this.showEmailLoginSpinner = false;
        });
    },
    resendVerificationEmail() {
      let user = firebase.auth().currentUser;
      user.reload().then(() => {
        user
          .sendEmailVerification()
          .then(() => {
            // Email sent.
            this.emailVerificationResent = true;
            this.showEmailVerificationError = false;
            this.showLogInForm = false;
          })
          .catch((error) => {
            // An error happened.
            console.log(error.message);
          });
      });
    },
    forgotPassword() {
      this.showLogInForm = false;
      this.showForgotPasswordForm = true;
    },
    sendPasswordResetEmail() {
      let auth = firebase.auth();
      auth
        .sendPasswordResetEmail(this.logInEmail)
        .then(() => {
          this.showForgotPasswordForm = false;
          this.showResentMailConfirmation = true;
        })
        .catch((error) => {
          this.forgotPasswordFeedback = error.message;
        });
    },
    closeDialogAndSignOut() {
      this.showEmailLoginSpinner = false;
      firebase.auth().signOut().then(() => {
        this.showLogInDialog = false;
        // Show snackbar
        this.$store.dispatch('setSnackbar', {
            timeout: 5000,
            text: 'You have been signed out. Verify your email and sign in again'
          });
      });
    }
  },
  computed: {
    breakpoint() {
      return this.$vuetify.breakpoint;
    },
    showLogInDialog: {
      get() {
        return this.$store.getters.getShowLogInDialog;
      },
      set(value) {
        this.$store.dispatch("setShowLogInDialog", value);
      },
    },
  },
  watch: {
    showLogInDialog() {
      this.$refs.form.resetValidation();
      this.$refs.resetPasswordForm.resetValidation();
      // this.feedback = null;
      this.logInEmail = "";
      this.logInPassword = "";
      this.feedback = "";
      this.forgotPasswordFeedback = "";

      this.showLogInForm = true;
      this.showEmailVerificationError = false;
      this.emailVerificationResent = false;
      this.showForgotPasswordForm = false;
      this.showResentMailConfirmation = false;
    },
  },
};
</script>

<style>
.continue-with {
  width: 100%;
  text-align: center;
  border-bottom: 1px solid #ddd;
  line-height: 0.1em;
  margin: 10px 0 20px;
}
.continue-with span {
  background-color: #fff;
  padding: 0 10px;
}
</style>